// Lightweight first-party page-view tracking for the Proteo admin dashboard.
// Sends events to the shared API server; failures are silent and never affect UX.

const SESSION_KEY = 'proteo-session-id';

const LANDING_PATH_KEY = 'proteo-landing-path';

const QA_SESSION_KEY = 'proteo-analytics-qa';

export type GooglePlayPlacement =
  | 'navigation'
  | 'hero'
  | 'pricing'
  | 'footer'
  | 'hub'
  | 'article_footer';
function createIdentifier(prefix: string): string {
  const suffix =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
  return `${prefix}_${suffix}`;
}

function getSessionId(): string {
  try {
    let id = localStorage.getItem(SESSION_KEY);
    if (!id) {
      id = createIdentifier('web');
      localStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    // Storage may be unavailable in private browsing. A per-page random ID is
    // still a useful anonymous session; a shared "anonymous" ID is not.
    return createIdentifier('web');
  }
}

function safePath(path: string): string {
  const pathname = path.split(/[?#]/, 1)[0] || '/';
  return pathname.startsWith('/') ? pathname.slice(0, 512) : '/';
}

function internalReleaseCheckMarker(): string | null {
  try {
    const queryMarker = new URLSearchParams(window.location.search).get('proteo_qa')?.trim();
    if (
      queryMarker &&
      (queryMarker === '1' ||
        queryMarker === 'true' ||
        queryMarker === 'qa' ||
        /^v1:\d{4}-\d{2}-\d{2}:[a-f0-9]{64}$/i.test(queryMarker))
    ) {
      sessionStorage.setItem(QA_SESSION_KEY, queryMarker);
      return queryMarker;
    }
    return sessionStorage.getItem(QA_SESSION_KEY);
  } catch {
    // If browser storage is unavailable, only the explicit URL marker can
    // reliably classify this page's event as an internal release check.
    return new URLSearchParams(window.location.search).get('proteo_qa');
  }
}
export function trackPageView(path: string): void {
  trackSiteEvent('page_view', path);
}

export function trackGooglePlayClick(placement: GooglePlayPlacement): void {
  trackSiteEvent('google_play_click', window.location.pathname, placement);
}

/** Public Google Play listing used by every marketing-site install CTA. */
export const GOOGLE_PLAY_URL = 'https://play.google.com/store/apps/details?id=com.proteo.app';

function shouldSkipAnalytics(): boolean {
  try {
    return navigator.webdriver;
  } catch {
    return typeof navigator !== 'undefined' && navigator.webdriver;
  }
}

function trackSiteEvent(
  type: 'page_view' | 'google_play_click',
  path: string,
  placement?: GooglePlayPlacement,
): void {
  try {
    if (shouldSkipAnalytics()) return;
    const internalReleaseCheck = internalReleaseCheckMarker();
    const referrer = document.referrer
      ? new URL(document.referrer).hostname
      : '';
    // Skip self-referrals so internal navigation doesn't pollute referrer stats
    const isSelf = referrer === window.location.hostname;
    const safeEventPath = safePath(path);
    const { firstTouch, lastTouch } = getAttribution();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (internalReleaseCheck) {
      headers['X-Proteo-Internal-Check'] = internalReleaseCheck;
    }
    void fetch(`${import.meta.env.BASE_URL}api/events`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        source: 'web',
        type,
        eventId: createIdentifier('event'),
        sessionId: getSessionId(),
        path: safeEventPath,
        landingPath: getLandingPath(safeEventPath),
        referrer: isSelf ? undefined : referrer || undefined,
        placement,
        firstTouchSource: firstTouch?.source,
        firstTouchMedium: firstTouch?.medium,
        firstTouchCampaign: firstTouch?.campaign,
        lastTouchSource: lastTouch?.source,
        lastTouchMedium: lastTouch?.medium,
        lastTouchCampaign: lastTouch?.campaign,
      }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    // never break the site over analytics
  }
}

type Attribution = {
  firstTouch?: CampaignTouch;
  lastTouch?: CampaignTouch;
};

function getLandingPath(path: string): string {
  try {
    const stored = sessionStorage.getItem(LANDING_PATH_KEY);
    if (stored) return safePath(stored);
    const landingPath = safePath(path);
    sessionStorage.setItem(LANDING_PATH_KEY, landingPath);
    return landingPath;
  } catch {
    return safePath(path);
  }
}

function readJson<T>(key: string): T | undefined {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : undefined;
  } catch {
    return undefined;
  }
}

function normalizeUtm(value: string | null): string | undefined {
  const normalized = value?.trim().toLowerCase();
  return normalized && /^[a-z0-9][a-z0-9._~-]{0,99}$/.test(normalized)
    ? normalized
    : undefined;
}

type CampaignTouch = {
  source?: string;
  medium?: string;
  campaign?: string;
};

function currentUtm(): CampaignTouch | undefined {
  const params = new URLSearchParams(window.location.search);
  const touch = {
    source: normalizeUtm(params.get('utm_source')),
    medium: normalizeUtm(params.get('utm_medium')),
    campaign: normalizeUtm(params.get('utm_campaign')),
  };
  return Object.values(touch).some(Boolean) ? touch : undefined;
}

const ATTRIBUTION_KEY = 'proteo-utm-attribution-v1';

function getAttribution(): Attribution {
  const stored = readJson<Attribution>(ATTRIBUTION_KEY) ?? {};
  const incoming = currentUtm();
  if (incoming) {
    const next: Attribution = {
      firstTouch: stored.firstTouch ?? incoming,
      lastTouch: incoming,
    };
    try {
      localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(next));
    } catch {
      // Analytics storage is best-effort.
    }
    return next;
  }
  return stored;
}
